import React, {
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  Ref,
  useRef,
} from 'react';
import { DataTable } from '@abyss/web/ui/DataTable';
import { Grid } from '@abyss/web/ui/Grid';
import { Link } from '@abyss/web/ui/Link';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Heading } from '@abyss/web/ui/Heading';
import { Layout } from '@abyss/web/ui/Layout';
import { Button } from '@abyss/web/ui/Button';
import { useDataTable } from '@abyss/web/hooks/useDataTable';
import { DateService } from '@src/api/common/DateService';
import { UserService } from '@src/api/common/UserService';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { LocalStorageService } from '@src/api/common/LocalStorageService';
import { useLoadingStore } from '@src/store/common/LoadingStore';
import { useOverlay } from '@abyss/web/hooks/useOverlay';
import {
  IGridBuilderProps,
  IGridBuilderRef,
} from './interfaces/IGridBuilderProps';

interface IStorageConfig {
  filters: any;
  pageSize?: number;
  pageIndex?: number;
  globalFilter?: string;
  datasetFilters?: any[];
}

interface GridBuilderProps {
  props: IGridBuilderProps;
}

const UTF8_BOM = '\uFEFF';

const downloadCsvWithBom = (
  dataRows: any[],
  columns: any[],
  omitColumns: string[] = [],
  fileName = 'tableData.csv'
) => {
  const exportColumns = (columns || []).filter(
    (col) =>
      typeof col?.Header === 'string' &&
      col.id !== 'individualActions' &&
      !omitColumns.includes(col.id)
  );

  const escape = (value: any) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const header = exportColumns.map((col) => escape(col.Header)).join(',');
  const body = (dataRows || []).map((row) => {
    const rowData = row?.values || row;
    return exportColumns
      .map((col) =>
        col.customSetCsv
          ? col.customSetCsv({ value: rowData?.[col.id], row: rowData })
          : rowData?.[col.id]
      )
      .map(escape)
      .join(',');
  });

  const content = UTF8_BOM + [header, ...body].join('\r\n');
  const baseName = fileName.toLowerCase().endsWith('.csv')
    ? fileName.slice(0, -4)
    : fileName;
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${baseName}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
const CsvFormulaChars = /^[=+\-@\t\r]/;

const SanitizeCsvValue = (value: any): any => {
  if (typeof value === 'string' && CsvFormulaChars.test(value)) {
    return `\t${value}`;
  }
  return value;
};

export const GridBuilder = forwardRef(
  ({ props }: GridBuilderProps, ref?: Ref<IGridBuilderRef>) => {
    const user = UserService.GetUser();
    const defaultStorageConfig: IStorageConfig = {
      filters: {},
      pageSize: 5,
      pageIndex: 0,
      globalFilter: '',
      datasetFilters: [],
    };
    const { navigate } = useRouter();
    const [customFileName, setCustomFileName] = useState<string | null>(null);
    const [isFirstTime, setIsFirstTime] = useState(true);
    const [storageConfig, setStorageConfig] =
      useState<IStorageConfig>(defaultStorageConfig);
    const [columnState, setColumnState] = useState<any[]>([]);
    const [headerButtons, setHeaderButtons] = useState<any[]>([]);

    const IsDateTimeValue = (str) => {
      return DateService.IsValidDateTime(str);
    };

    const formatExport = ({ value }) => {
      if (value === null || value === undefined) {
        return '';
      }

      if (value instanceof Date) {
        return DateService.ToDate(value.toISOString());
      }

      if (IsDateTimeValue(value)) {
        return DateService.ToDate(value);
      }

      return SanitizeCsvValue(value);
    };

    const DataSource = {
      GetColumns: (): Array<any> => {
        const columnsArray: Array<any> = [];
        props.columns.forEach((element) => {
          const col: any = {
            Header: element.heading,
            accessor: element.field,
            customSetCsv: formatExport,
            canToggleVisibilty: true,
            canReorderColumn: true,
            disableResizing:
              element.disableResize === undefined
                ? false
                : element.disableResize,
            width: element.width,
            isHiddenByDefault:
              element.hidden === undefined ? false : element.hidden,
            disableSortBy:
              element.disableSort === undefined ? false : element.disableSort,
          };

          if (element.isDate === true) {
            col.Cell = ({ value }) => {
              if (!value) return '';

              const formattedDate =
                element.dateFormat !== undefined
                  ? DateService.ToCustomFormat(value, element.dateFormat)
                  : DateService.ToDateTime(value);

              return (
                <div
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    minWidth: '95px',
                  }}
                >
                  {formattedDate}
                </div>
              );
            };
            col.sortType = (rowA, rowB, columnId) => {
              const a = rowA.values[columnId];
              const b = rowB.values[columnId];

              if (!a && !b) return 0;
              if (!a) return -1;
              if (!b) return 1;

              return new Date(a).getTime() - new Date(b).getTime();
            };
            col.hiddenDefaultFilters = ['contains', 'starts-with', 'ends-with'];
          }

          if (element.link !== undefined) {
            col.Cell = ({ value, row }) => {
              return (
                value !== undefined &&
                value !== null &&
                value !== '' && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: `${element.link?.alignText || 'center'}`,
                      width: '100%',
                    }}
                  >
                    <Link
                      fontWeight="soft"
                      size="sm"
                      href={
                        element.link?.appendValueInUrl === true
                          ? `${element.link?.url}${value}`
                          : element.link?.appendPropertyName
                            ? `${element.link?.url}${row.original[element.link.appendPropertyName]}`
                            : element.link?.url
                      }
                      openNewWindow={element.link?.openNewWindow}
                      css={{
                        display: 'inline-flex',
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '1px',
                          backgroundColor: 'currentColor',
                          transform: 'scaleX(0)',
                          transformOrigin: 'center',
                          transition: 'transform 0.2s ease-in-out',
                        },
                        '&:hover::before': {
                          transform: 'scaleX(1)',
                        },
                      }}
                    >
                      <React.Fragment>
                        {element.link?.icon !== undefined ? (
                          <IconMaterial icon={element.link?.icon} size="24px" />
                        ) : null}
                        {element.link?.showValue === true ? value : null}
                      </React.Fragment>
                    </Link>
                  </div>
                )
              );
            };
          }

          if (element.cell !== undefined) {
            col.Cell = element.cell;
          }

          if (element.accessor !== undefined) {
            col.accessor = element.accessor;
          }

          columnsArray.push(col);
        });

        return columnsArray;
      },
    };

    const originalColumns = useMemo(() => DataSource.GetColumns(), []);

    const columns = useMemo(() => {
      if (columnState.length === 0) {
        return DataSource.GetColumns();
      } else {
        return originalColumns;
      }
    }, [columnState]);

    const filterColumnTypes = useMemo(() => {
      const types = {};

      props.columns.forEach((column) => {
        if (column.isDate === true && column.field) {
          types[column.field] = {
            type: 'date',
          };
        }
      });

      return types;
    }, [props.columns]);

    const CustomExportHandler = props.customExport?.custom
      ? {
          ...props.customExport?.custom,
          icon: <IconSymbol icon="download_done" />,
          onClick: () => {
            props.customExport?.custom?.onClick().then((res) => {
              if (res && res.results) {
                dataTableProps.downloadCSV(
                  res?.results,
                  dataTableProps.reactTableProps.visibleColumns,
                  dataTableProps.removeCsvColumns,
                  dataTableProps.downloadButtonConfig?.fullCsvFileName
                );
              }
            });
          },
        }
      : undefined;

    const downloadButtonConfig = useMemo(() => {
      if (props.customDownloadFileNameGetter && customFileName) {
        return {
          filteredCsvFileName: `${customFileName}-filtered.csv`,
          fullCsvFileName: `${customFileName}-full.csv`,
          removeFiltered: props.customExport?.removeFiltered || false,
          removeFull: props.customExport?.removeFull || false,
          custom: CustomExportHandler,
        };
      } else {
        const date = new Date();
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();
        const dateString = `${year}-${month}-${day}-${hours}-${minutes}-${seconds}`;

        return {
          filteredCsvFileName: `${props.gridHeading?.replace(
            ' ',
            '-'
          )}-${dateString}-filtered.csv`,
          fullCsvFileName: `${props.gridHeading?.replace(
            ' ',
            '-'
          )}-${dateString}-full.csv`,
          removeFiltered: props.customExport?.removeFiltered || false,
          removeFull: props.customExport?.removeFull || false,
          custom: CustomExportHandler,
        };
      }
    }, [customFileName, props.gridHeading]);

    const date = new Date();
    const datestr =
      date.getFullYear() +
      '-' +
      (date.getMonth() + 1) +
      '-' +
      date.getDate() +
      '-' +
      date.getHours() +
      '-' +
      date.getMinutes() +
      '-' +
      date.getSeconds();

    const DataTypeConversion = (data: any) => {
      if (Array.isArray(data)) {
        return data.map((item) => DataTypeConversion(item));
      } else if (typeof data === 'object' && data !== null) {
        return Object.keys(data).reduce((acc, key) => {
          acc[key] =
            typeof data[key] === 'boolean'
              ? data[key].toString()
              : data[key] === null
                ? ''
                : DataTypeConversion(data[key]);

          const isDateColumn = props.columns.some(
            (col) => col.field === key && col.isDate === true
          );

          if (isDateColumn && data[key]) {
            acc[key] = IsDateTimeValue(data[key])
              ? new Date(data[key])
              : data[key];
          } else if (key.includes('Date') && data[key]) {
            acc[key] = IsDateTimeValue(data[key])
              ? new Date(data[key])
              : data[key];
          }

          return acc;
        }, {} as any);
      }

      return data;
    };

    let dataTableProps = useDataTable({
      uniqueStorageId: `${props.gridOptionName}-user`,
      initialData:
        props.data && props.data.length > 0
          ? DataTypeConversion(props.data)
          : [],
      initialColumns: columns,
      showColumnVisibilityConfig:
        props.showColumnVisibilityConfig === undefined
          ? true
          : props.showColumnVisibilityConfig,
      showPagination:
        props.showPagination === undefined ? true : props.showPagination,
      showGlobalFilter:
        props.showGlobalFilter === undefined ? true : props.showGlobalFilter,
      showFilterDataset:
        props.showFilterDataset === undefined ? true : props.showFilterDataset,
      showTableSettings:
        props.showTableSettings === undefined ? true : props.showTableSettings,
      showDownloadButton: false,
      highlightRowOnHover:
        props.highlightRowOnHover === undefined
          ? false
          : props.highlightRowOnHover,
      showFullscreenButton:
        props.showFullscreenButton === undefined
          ? false
          : props.showFullscreenButton,
      showBottomPagination:
        props.showBottomPagination === undefined
          ? false
          : props.showBottomPagination,
      showTopPagination:
        props.showTopPagination === undefined ? true : props.showTopPagination,
      showColumnSort:
        props.showColumnSort === undefined ? true : props.showColumnSort,
      showSelection:
        props.showSelection === undefined ? false : props.showSelection,
      removeCsvColumns: props?.removeCsvColumns
        ? props.removeCsvColumns
        : ['id'],
      removeFilterColumns: props?.removeFilterColumns
        ? props.removeFilterColumns
        : ['id'],
      filterColumnTypes: filterColumnTypes,
      downloadButtonConfig: downloadButtonConfig,
      minColumnWidth: 50,
      pageSizeOptions: [5, 10, 25, 50, 100],
      initialGlobalFilter: storageConfig.globalFilter,
      initialFilters: [],
      customHeaderButtons: headerButtons,
    });

    dataTableProps.columnMgmt.columnOrder = originalColumns;
    dataTableProps.downloadCSV = downloadCsvWithBom;

    const Action = {
      ReloadGrid: async (formData?: any) => {
        if (!props.data) {
          const data = await props.gridApiMethod?.(formData);
          const formattedData = DataTypeConversion(data);
          dataTableProps.setData(formattedData);
          return data;
        }
      },
      Refetch: async (formData?: any) => {
        const data = await props.gridApiMethod?.(formData);
        const formattedData = DataTypeConversion(data);
        dataTableProps.setData(formattedData);
        return data;
      },
      UpdateFilterStorage: () => {
        const body = props.form.getValues();
        setStorageConfig((s) => {
          return {
            ...s,
            filters: body,
          };
        });
      },
      UpdateGridStateStorage: async () => {
        if (!isFirstTime) {
          await setStorageConfig((s) => {
            return {
              ...s,
              pageSize: dataTableProps.state.pageSize,
              pageIndex: dataTableProps.state.pageIndex,
              globalFilter: dataTableProps.state.globalFilter,
              datasetFilters: dataTableProps.state.filters,
            };
          });
        }
        setIsFirstTime(false);
      },
      Reset: async () => {
        if (typeof props.onReset === 'function') {
          props.onReset();
        } else {
          props.form.reset();
        }
        Action.UpdateFilterStorage();
        await Action.ReloadGrid(props.form);
        if (props.onFilterClick) {
          await props.onFilterClick(props.form);
        }
      },
      Filter: async () => {
        Action.UpdateFilterStorage();
        await Action.ReloadGrid();
        if (props.onFilterClick) {
          await props.onFilterClick(props.form);
        }
      },
      ClearPrefrences: async () => {
        let loaderState = GetLoaderState(true);
        await setLoading(loaderState);

        await LocalStorageService.Set(
          `${props.gridOptionName}-preferences-cleared`,
          true
        );

        await Action.ResetColumns();
        setShowTableSettings(true);
        setTimeout(() => {
          setShowTableSettings(false);
        }, 200);

        setTimeout(() => {
          loaderState = GetLoaderState(false);
          setLoading(loaderState);
        }, 1000);
      },
      Refresh: async () => {
        await Action.Refetch();
      },
      ResetColumns: async () => {
        const storageName = `${props.gridOptionName}-user`;
        let allColumnsCopy = [...dataTableProps.columnMgmt.allColumns];
        let counter = 1;
        originalColumns.forEach((element) => {
          let columnCopy = allColumnsCopy.find(
            (c) => c.key === element.accessor
          );
          if (columnCopy) {
            columnCopy.order = counter;
            dataTableProps.columnMgmt.allColumns[counter - 1] = columnCopy;
          }
          counter++;
        });
        await dataTableProps.columnMgmt.setColumnOrder(
          dataTableProps.columnMgmt.columnOrder
        );
        await setColumnState(originalColumns);
        await dataTableProps.setColumns(originalColumns);
        dataTableProps.columnMgmt.columnOrder = originalColumns;
        LocalStorageService.Remove(storageName);
      },
      BulkInsert: async () => {
        await props.setShowSelection(!props.showSelection);

        let originalColumnsCopy = [...originalColumns];
        if (!props.showSelection) {
          originalColumnsCopy.splice(0, 1);
        }

        setColumnState(originalColumnsCopy);
        dataTableProps.setColumns(originalColumnsCopy);
      },
    };

    const headerButtonsMemo = useMemo(
      () => [
        {
          label: 'Grid Menu',
          icon: <IconSymbol icon="menu" />,
          dropdownItems: [
            props.createNewButton
              ? {
                  title: props.createNewButton?.text ?? 'Create New',
                  style: { cursor: 'pointer' },
                  onClick: () => {
                    navigate(
                      props.createNewButton?.url
                        ? props.createNewButton.url
                        : '/route'
                    );
                  },
                  disabled: props.createNewButton?.disabled ?? false,
                }
              : {},
            props.showSelection !== undefined
              ? {
                  title: props.showSelection
                    ? (props.createBulkInsertButton?.deActivateText ??
                      'Deactivate Bulk Edit')
                    : (props.createBulkInsertButton?.activateText ??
                      'Activate Bulk Edit'),
                  style: { cursor: 'pointer' },
                  onClick: () => {
                    const isSelectionEnabled =
                      props.showSelection !== undefined && props.showSelection;

                    if (isSelectionEnabled) {
                      const { tableActions } = dataTableProps.reactTableProps;
                      const selectedRows = tableActions.getSelectedRows();
                      const selectedRowIds = tableActions.getSelectedRowIds();

                      if (
                        Array.isArray(selectedRows) &&
                        selectedRowIds?.length > 0
                      ) {
                        selectedRows.forEach((row, index) => {
                          const rowId = selectedRowIds[index];
                          if (rowId !== undefined && row !== undefined) {
                            tableActions.modifyRow(rowId, row);
                          }
                        });
                      }
                    }

                    Action.BulkInsert();
                  },
                }
              : {},
            ...((props.showFilterDataset === undefined
              ? true
              : props.showFilterDataset) !== false
              ? [
                  {
                    title: 'Filter Dataset',
                    style: { cursor: 'pointer' },
                    onClick: () => {
                      const filterDatasetButton = document.querySelector(
                        '.abyss-data-table-filter-dataset-button'
                      ) as HTMLElement;
                      if (filterDatasetButton) {
                        filterDatasetButton.click();
                      }
                    },
                  },
                ]
              : []),
            ...((props.showTableSettings === undefined
              ? true
              : props.showTableSettings) !== false
              ? [
                  {
                    title: 'Table Settings',
                    style: { cursor: 'pointer' },
                    onClick: () => {
                      const tableSettingsButton = document.querySelector(
                        '.abyss-data-table-table-settings-button'
                      ) as HTMLElement;
                      if (tableSettingsButton) {
                        tableSettingsButton.click();
                      }
                    },
                  },
                ]
              : []),
            !props.customExport?.removeFiltered
              ? {
                  title: 'Download Filtered Dataset(CSV)',
                  style: { cursor: 'pointer' },
                  onClick: () => {
                    dataTableProps.downloadCSV(
                      dataTableProps.data,
                      dataTableProps.reactTableProps.visibleColumns,
                      dataTableProps.removeCsvColumns,
                      downloadButtonConfig.filteredCsvFileName
                    );
                  },
                }
              : {},
            !props.customExport?.removeFull
              ? {
                  title: 'Download Full Dataset(CSV)',
                  style: { cursor: 'pointer' },
                  onClick: () => {
                    dataTableProps.downloadCSV(
                      dataTableProps.data,
                      dataTableProps.reactTableProps.visibleColumns,
                      dataTableProps.removeCsvColumns,
                      downloadButtonConfig.fullCsvFileName
                    );
                  },
                }
              : {},
            props.customExport?.custom
              ? {
                  title: props.customExport.custom?.title,
                  style: { cursor: 'pointer' },
                  onClick: () => {
                    props.customExport?.custom?.onClick().then((res) => {
                      if (res && res.results) {
                        dataTableProps.downloadCSV(
                          res?.results,
                          dataTableProps.reactTableProps.visibleColumns,
                          dataTableProps.removeCsvColumns,
                          downloadButtonConfig.fullCsvFileName
                        );
                      }
                    });
                  },
                }
              : {},
            {
              title: 'Clear Preferences',
              style: { cursor: 'pointer' },
              onClick: () => {
                Action.ClearPrefrences();
              },
            },
            {
              title: 'Refresh',
              style: { cursor: 'pointer' },
              onClick: () => {
                Action.Refresh();
              },
            },
          ].filter((item) => Object.keys(item).length > 0),
        },
        props.showSelection &&
        props.createBulkInsertButton?.showEditButton !== false
          ? {
              label:
                props.createBulkInsertButton?.bulkActionEdit?.text ?? 'Edit',
              onClick: () => {
                const selectedRows =
                  dataTableProps.state.selectedFlatRows?.map(
                    (row) => row.original.id
                  ) ?? [];
                props.createBulkInsertButton?.bulkActionEdit?.handler(
                  selectedRows
                );
              },
            }
          : {},
        props.showSelection &&
        props.createBulkInsertButton?.showDeleteButton !== false
          ? {
              label:
                props.createBulkInsertButton?.bulkActionDelete?.text ??
                'Delete',
              onClick: () => {
                const selectedRows =
                  dataTableProps.state.selectedFlatRows?.map(
                    (row) => row.original.id
                  ) ?? [];
                props.createBulkInsertButton?.bulkActionDelete?.handler(
                  selectedRows
                );
              },
            }
          : {},
      ],
      [props, downloadButtonConfig]
    );

    useEffect(() => {
      const finalHeaderButtons = props.customHeaderButtons
        ? [...headerButtonsMemo, ...props.customHeaderButtons]
        : headerButtonsMemo;

      setHeaderButtons(finalHeaderButtons);
    }, [headerButtonsMemo, props.customHeaderButtons]);

    const [showTableSettings, setShowTableSettings] = useState(false);
    const loadingState = useLoadingStore('loadingState');
    const setLoading = useLoadingStore('setLoading');
    const loadingOverlay = useOverlay('crdp-loading');

    const GetLoaderState = (isLoading: boolean) => {
      if (isLoading === true) {
        loadingState.count += 1;
      } else {
        loadingState.count -= 1;
      }
      loadingState.loading = loadingState.count > 0;
      if (loadingState.loading === true) {
        loadingOverlay?.open();
      } else {
        loadingOverlay?.close();
      }
      return loadingState;
    };

    useEffect(() => {
      const load = async () => {
        const preferencesCleared = LocalStorageService.Get(
          `${props.gridOptionName}-preferences-cleared`
        );
        if (preferencesCleared) {
          setStorageConfig(defaultStorageConfig);
          dataTableProps.setColumns(originalColumns);
          LocalStorageService.Remove(
            `${props.gridOptionName}-preferences-cleared`
          );
          LocalStorageService.Set(
            `${props.gridOptionName}-filters`,
            defaultStorageConfig
          );
        } else {
          const storageData = LocalStorageService.Get(
            `${props.gridOptionName}-filters`
          );
          if (storageData !== undefined && storageData !== null) {
            await setStorageConfig(storageData);
            await dataTableProps.pagination.setPageSize(storageData.pageSize);
            for (const key in storageData.filters) {
              if (storageData?.filters.hasOwnProperty(key)) {
                const element = storageData.filters[key];
                props.form.setValue(key, element);
              }
            }
            setTimeout(async () => {
              await dataTableProps.pagination.gotoPage(storageData.pageIndex);
            }, 100);
          } else {
            setStorageConfig(defaultStorageConfig);
            dataTableProps.setColumns(originalColumns);
          }
        }
        if (!props.onDemandData) {
          await Action.ReloadGrid();
        }
      };
      load();
    }, []);

    useEffect(() => {
      const storageData = LocalStorageService.Get(
        `${props.gridOptionName}-filters`
      );
      if (storageData) {
        if (dataTableProps.data && storageData?.datasetFilters?.length > 0) {
          dataTableProps.filter.setAllFilters(storageData.datasetFilters);
        }
        if (dataTableProps.data && storageData?.globalFilter) {
          dataTableProps.filter.setGlobalFilter(storageData.globalFilter);
        }
      }
    }, [dataTableProps.data]);

    useEffect(() => {
      if (
        !LocalStorageService.Get(`${props.gridOptionName}-preferences-cleared`)
      ) {
        LocalStorageService.Set(
          `${props.gridOptionName}-filters`,
          storageConfig
        );
      }
    }, [storageConfig]);

    useEffect(() => {
      setTimeout(async () => {
        await Action.UpdateGridStateStorage();
      }, 200);
    }, [
      dataTableProps.state.pageSize,
      dataTableProps.state.pageIndex,
      dataTableProps.state.globalFilter,
      dataTableProps.state.filters,
    ]);

    useEffect(() => {
      if (showTableSettings) {
        const tableSettingsButton = document.querySelector(
          '.abyss-data-table-table-settings-button'
        ) as HTMLElement;
        if (tableSettingsButton) {
          tableSettingsButton.click();
          setTimeout(() => {
            const closeButton = document.querySelector(
              '.abyss-modal-close-button'
            ) as HTMLElement;
            if (closeButton) {
              closeButton.click();
            }
          }, 100);
        }
      }
    }, [showTableSettings]);

    useEffect(() => {
      const fetchCustomFileName = async () => {
        if (props.customDownloadFileNameGetter) {
          try {
            const fileName = await props.customDownloadFileNameGetter();
            setCustomFileName(fileName || '');
          } catch (error) {
            setCustomFileName('');
          }
        }
      };
      fetchCustomFileName();
    }, []);

    useImperativeHandle(ref, () => ({
      ReloadGrid: async (formData?: any) => {
        return await Action.ReloadGrid(formData);
      },
      SetGridColumns: (columns: any[]) => {
        dataTableProps.setColumns(columns);
      },
      GetData: () => {
        return dataTableProps.data ? dataTableProps.data : [];
      },
      FilterGrid: async () => {
        return await Action.Filter();
      },
      GetSelectedData: () => {
        return (
          dataTableProps.state.selectedFlatRows?.map(
            (row) => row.original.id
          ) ?? []
        );
      },
      Refetch: async (formData?: any) => {
        return await Action.Refetch(formData);
      },
    }));

    useEffect(() => {
      const input = document.querySelector(
        '.abyss-data-table-global-search.abyss-text-input'
      );
      const button = document.querySelector(
        '.abyss-data-table-global-search.abyss-text-input-action-button'
      );
      if (input && button) {
        const handler = (e) => {
          if (e.key === 'Enter') {
            (button as HTMLElement).click();
            e.preventDefault();
          }
        };
        input.addEventListener('keydown', handler);
        return () => input.removeEventListener('keydown', handler);
      }
    }, []);

    return (
      <React.Fragment>
        <Grid>
          {props.hideFilter ? null : (
            <Grid.Col span={{ xs: 12 }}>
              <Heading display="h4" color="$primary1">
                <Layout.Group>
                  <Button type="submit" size="$md" onClick={Action.Filter}>
                    {props?.filterButtons?.filterButtonText ?? 'Filter'}
                  </Button>
                  <Button size="$md" variant="outline" onClick={Action.Reset}>
                    {props?.filterButtons?.clearFilterButtonText ?? 'Reset'}
                  </Button>
                </Layout.Group>
              </Heading>
            </Grid.Col>
          )}
          <Grid.Col span={{ xs: '100%' }}>
            <DataTable
              tableState={dataTableProps}
              title=""
              hideTitleHeader
              css={props.customCss ? props.customCss : {}}
            />
          </Grid.Col>
        </Grid>
      </React.Fragment>
    );
  }
);
